import { useSelector, shallowEqual } from 'react-redux';
import { fieldTypes, descriptionIssues, SOFTWARE, DESCRIPTION } from '@/helpers/constants';

const noLangFields = [fieldTypes.CALENDAR, fieldTypes.DROPDOWN, fieldTypes.MULTI_SELECTION, fieldTypes.ACCORDION,
  fieldTypes.SWITCH, fieldTypes.INPUT_NUMBER, fieldTypes.CUSTOM_ARRAY, fieldTypes.INPUT_USER, fieldTypes.CHIP, fieldTypes.CHECKBOX];

const fieldsRequiringUppercaseHandling = ['zgs', 'qnt', 'kem'];

export const checkIfTermNotAvailableInLanguaje = (field, lang) => {
  if (field && field.path?.includes('translations')) {
    return field.path?.split('.')?.find((subPath) => subPath.startsWith('translations')
      && subPath.replace('translations', '') === `[${lang}]`) === undefined;
  }

  return false;
};

export const getTabIndex = (tab) => tab.split('[')[1].replace(']', '');

// fieldPath prop from input
const useCampaignSearchSelected = (fieldPath = [], fieldType, noTranslationField = true) => {
  const { headerLanguage, campaignSearch } = useSelector((state) => ({
    headerLanguage: state.headerReducer.language || state.campaignReducer?.info?.defaultLanguage,
    campaignSearch: state.campaignReducer.campaignSearch,
  }), shallowEqual);

  // fieldPath from back
  const fieldSelectedArrayOriginal = campaignSearch?.fieldSelected?.field?.path?.split('.').slice(0, -1) || [];
  const fieldSelectedArray = campaignSearch?.fieldSelected?.field?.path?.split('.').slice(0, -1) || [];

  let termNotAvailableInLanguage = false;
  let campaignSearchSelected = false;
  let fieldSelectedNoLang = false;

  if (campaignSearch?.active && campaignSearch?.resultCounter > 0) {
    if (fieldPath?.length === 0 || !campaignSearch?.fieldSelected?.field?.path) return { campaignSearchSelected, termNotAvailableInLanguage };

    // Corner case for campaing header user chips
    if (campaignSearch?.fieldSelected?.tab === 'campaign' && campaignSearch?.fieldSelected?.field?.path?.includes('summary')) {
      campaignSearchSelected = fieldPath.join('.') === fieldSelectedArrayOriginal.join('.');

      return { campaignSearchSelected, termNotAvailableInLanguage };
    }
    // Remove unnecessary segments of the openSearch fieldPath

    if (!['affectedHardware', 'affectedVehicles', 'recipients', 'feedback'].includes(campaignSearch?.fieldSelected?.tab)
      && fieldSelectedArrayOriginal[0] !== 'flashingInformation' && fieldSelectedArrayOriginal[0] !== SOFTWARE.OTA_COMMENT) { fieldSelectedArray.shift(); }

    if (['softwareWithFlashing', 'softwareWithCoding', 'softwareWithDoorman'].includes(fieldSelectedArrayOriginal[1])) {
      fieldSelectedArray.shift();
    }

    let fieldSelectedPath = `${campaignSearch?.fieldSelected?.tab}.${fieldSelectedArray.join('.')}`;
    let fieldPathString = fieldPath?.join('.');

    if (fieldPath[0] === 'creationUser') { fieldSelectedPath = `creationUser.${fieldSelectedArray.join('.')}`; }
    // Check if the field has a match no matter the languaje
    fieldSelectedNoLang = fieldSelectedPath && fieldPathString && fieldSelectedPath.includes(fieldPathString);

    // Set termNotAvailableInLanguageto true if the field has a match in the translation
    if (headerLanguage && fieldSelectedNoLang) {
      termNotAvailableInLanguage = checkIfTermNotAvailableInLanguaje(campaignSearch?.fieldSelected?.field.value, headerLanguage);
    }

    // Corner case for Aff. Hardware and Software tables
    // Add the part prefix to the fieldPath and convert the first letter to uppercase -> description = partDescription
    // For the ZGS numbers add the part prefix and convert the first three letters to uppercase ->
    if (fieldPath[0] && !['diogenesName', 'hwPartNo', 'uuidCode', 'uuidCodeOld', 'ywpVersionOld', 'ywpVersionNew'].includes(fieldPath[1])
      && ['affectedHardwareWithoutPartChange', 'affectedHardwareWithPartChange', 'flashingPartList', 'flashingUuidList',
        'codingPartList', 'codingUuidList'].includes(fieldPath[0]?.split('[')[0])) {
      const tableType = ['flashingUuidList', 'codingUuidList'].includes(fieldPath[0]?.split('[')[0]) ? 'uuid' : 'part';
      fieldPath[1] = (fieldsRequiringUppercaseHandling.some((fieldException) => fieldPath[1].includes(fieldException)))
        ? `${tableType}${fieldPath[1]?.charAt(0).toUpperCase()}${fieldPath[1]?.charAt(1).toUpperCase()}${fieldPath[1]?.charAt(2).toUpperCase()}${fieldPath[1]?.slice(3)}`
        : `${tableType}${fieldPath[1]?.charAt(0).toUpperCase()}${fieldPath[1]?.slice(1)}`;
    }

    // If the field has no translation remove the translation segment from the local pathfield
    if (noTranslationField || noLangFields.includes(fieldType)) {
      fieldPathString = fieldPath.join('.');
      termNotAvailableInLanguage = false;
    } else {
      // Add translation segment to local pathfield
      fieldPathString = `${fieldPath.join('.')}.translations[${headerLanguage}]`;
    }

    // Corner case for description tab porblems section
    if (fieldSelectedArrayOriginal[0].includes(DESCRIPTION.PROBLEMS) && fieldPath[0] === DESCRIPTION.PROBLEMS) {
      const selectedTab = descriptionIssues.find((issue) => `${fieldPath[0]}[${issue}]` === fieldSelectedArrayOriginal[0]);

      termNotAvailableInLanguage = checkIfTermNotAvailableInLanguaje(campaignSearch?.fieldSelected?.field.value, headerLanguage);
      campaignSearchSelected = fieldSelectedArrayOriginal.join('.') === `${fieldPath[0]}[${selectedTab}].${fieldSelectedArray.join('.')}`;

      return { campaignSearchSelected, termNotAvailableInLanguage };
    }

    if (fieldSelectedArrayOriginal[0].includes('allModulesNotes') && fieldPath[0] === 'allModulesNotes') {
      campaignSearchSelected = fieldSelectedArrayOriginal.join('.') === `${fieldPath[0]}[${getTabIndex(fieldSelectedArrayOriginal[0])}].${fieldSelectedArray.join('.')}`;

      return { campaignSearchSelected, termNotAvailableInLanguage };
    }

    // Corner case for software tab - UUIDcode and UUIDcodeOld
    if (fieldSelectedArrayOriginal.some((path) => path?.includes('uuidCode'))) {
      campaignSearchSelected = fieldSelectedPath && fieldPathString && fieldSelectedPath.endsWith(fieldPathString);

      return { campaignSearchSelected, termNotAvailableInLanguage };
    }

    // If the local pathField and the openSearch fieldPath match set campaignSearchSelected to true
    campaignSearchSelected = fieldSelectedPath && fieldPathString && fieldSelectedPath.includes(fieldPathString);
  }

  return { campaignSearchSelected, termNotAvailableInLanguage };
};

export default useCampaignSearchSelected;
